import produce, { Draft } from "immer";
import { canImpersonate } from "pages/Rules/Form/Steps/CloudStorage/utils";
import { canUseStoreByUserAccount } from "pages/Rules/Form/Steps/CloudStorage/AdvancedOptions/UseStoreByUserAccount";
import { isOfficeIndividual } from "pages/Rules/Form/Steps/Target/Mailbox/utils";
import { isBlank } from "utils/string";
import { isEmptyArray } from "utils/isEmptyArray";
import { matchesStorageType } from "utils/storageProviders";
import validateStep from "./validateStep";
import {
  updateArchivingAddress,
  updateCloudStorage,
  updateEmailFlow,
  updateMailbox,
  updateApprovalSteps,
  updateWebhook,
  isCopyFilesAllowed,
} from "../helpers";
import {
  EmailFlow,
  Storage,
  ArchivingAddress,
  Mailbox,
  StorageWebhook,
  ProviderAccounts,
  ApprovalSteps,
  Content,
  Smime,
} from "@dashboard-v3/api";
import { ActionState } from "pages/Rules/Form/Steps/Actions/types";
import { State } from "../types";
import initStorageLocation from "../initStorageLocation";
import { WebhookCheckResult } from "../../Steps/Target/StorageWebhook/utils";
import {
  getCloudStorage as getStorage,
  getMailbox,
  typePath,
} from "pages/Rules/Form/context/ruleSelectors";

export function updateStep(state: State, payload: UpdateStep): State {
  if (payload.step === "action") {
    return produce(state, draft => {
      const ruleTypePath = typePath(draft.type, draft.rule);

      draft.steps.action.done = getActionStatus(draft, payload);

      if ("contentToSave" in ruleTypePath && "content" in payload.changes) {
        ruleTypePath.contentToSave.content = payload.changes.content;
        resetCopyFilesSetting(draft, payload.changes.content);
      }

      if ("trash" in ruleTypePath) {
        ruleTypePath.trash = payload.changes.trash;
      }

      if ("moveOriginalToTrashOnReplace" in ruleTypePath) {
        ruleTypePath.moveOriginalToTrashOnReplace =
          payload.changes.moveOriginalToTrashOnReplace;
      }

      if ("linksFormat" in ruleTypePath && "linksFormat" in payload.changes) {
        ruleTypePath.linksFormat = payload.changes.linksFormat;
      }

      if ("storageNotificationTemplateId" in payload.changes) {
        const storage = getStorage(draft.rule);
        storage.storageNotificationTemplateId =
          payload.changes.storageNotificationTemplateId;
      }

      if ("blockNotification" in ruleTypePath) {
        ruleTypePath.blockNotification = payload.changes.blockNofication;
      }

      if (
        "passwordNotificationToSender" in payload.changes &&
        "passwordNotificationToSender" in ruleTypePath
      ) {
        ruleTypePath.passwordNotificationToSender =
          payload.changes.passwordNotificationToSender;
      }

      if (
        "passwordNotificationToRecipient" in payload.changes &&
        "passwordNotificationToRecipient" in ruleTypePath
      ) {
        ruleTypePath.passwordNotificationToRecipient =
          payload.changes.passwordNotificationToRecipient;
      }

      if ("senderNotification" in payload.changes) {
        if (payload.changes.senderNotification === null) {
          delete ruleTypePath["senderNotification"];
        } else {
          ruleTypePath["senderNotification"] = {
            ...(ruleTypePath["senderNotification"] || {}),
            ...payload.changes.senderNotification,
          };
        }
      }

      if ("passwordNotificationText" in payload.changes) {
        if (payload.changes.passwordNotificationText === null) {
          delete ruleTypePath["passwordNotificationText"];
        } else {
          ruleTypePath["passwordNotificationText"] = {
            ...(ruleTypePath["passwordNotificationText"] || {}),
            ...payload.changes.passwordNotificationText,
          };
        }
      }

      validateStep("action", draft);
    });
  }

  if (payload.step === "smimeAction") {
    return produce(state, draft => {
      draft.steps.smimeAction.done = false;

      if ("smimeActions" in payload.changes) {
        draft.rule.smimeEmails.smimeActions = payload.changes.smimeActions;
      }

      if ("failedAction" in payload.changes) {
        draft.rule.smimeEmails.failedAction = payload.changes.failedAction;
      }

      validateStep("smimeAction", draft);
    });
  }

  if (payload.step === "target") {
    return produce(state, draft => {
      draft.steps.target.done = false;
      const target = state.rule.targetType;

      if (target === "ARCHIVING_ADDRESS") {
        updateArchivingAddress(
          draft.rule,
          payload.changes as Partial<ArchivingAddress>
        );

        if ("emailAddress" in payload.changes) {
          const sameOriginalEmail =
            draft.checks.archivingAddress.initialAddress ===
            payload.changes.emailAddress;

          draft.checks.archivingAddress.isValidEmail = sameOriginalEmail;
        }
      }

      if (target === "EMAIL_FLOW") {
        updateEmailFlow(
          draft.rule,
          payload.changes as
            | Partial<EmailFlow>
            | { saveForwardedMessages: boolean }
        );
      }

      if (target === "MAIL_BOX") {
        const mailbox = updateMailbox(
          draft.rule,
          payload.changes as Partial<Mailbox>
        );

        if (mailbox.provider !== "IMAP") {
          delete mailbox.imapServer;
        }

        if (isOfficeIndividual(mailbox)) {
          const changes = payload.changes as Partial<Mailbox>;
          const alreadySetLoginUser = Boolean(mailbox.loginUser);
          if (hasOneAccount(changes.accounts) && !alreadySetLoginUser) {
            mailbox.loginUser = changes.accounts.entities[0];
          }
        } else {
          delete mailbox.loginUser;
        }
      }

      if (target === "STORAGE_WEBHOOK") {
        updateWebhook(draft, payload.changes as Partial<StorageWebhookUpdate>);
      }

      validateStep("target", draft);
    });
  }

  if (payload.step === "cloudStorage") {
    return produce(state, draft => {
      draft.steps.cloudStorage.done = false;

      const prevProvider = getStorage(draft.rule).provider;
      const isProviderChanged =
        payload.changes.provider && payload.changes.provider !== prevProvider;

      const storage = updateCloudStorage(draft.rule, payload.changes);

      if (isProviderChanged) {
        storage.location = initStorageLocation(
          draft.rule.actionType,
          draft.rule.targetType,
          storage
        );
        validateStep("storageFilepath", draft);

        // A template belongs to one storage type, it can't survive the change
        if (!matchesStorageType(payload.changes.provider, prevProvider)) {
          delete storage.metadata;
        }
      }

      const shouldResetFolderLocation =
        Boolean(storage.storeId) && Boolean(storage.location?.parentId);

      if (shouldResetFolderLocation) {
        delete storage.location.parentId;
        delete storage.location?.parentName;
      }

      if (!canImpersonate(storage)) {
        draft.storageManagement = undefined;
      }

      if (!canUseStoreByUserAccount(draft)) {
        storage.allManagedUserIds = false;
      }

      const nullableFields = [
        "userId",
        "storeId",
        "storeIdUserId",
        "asUser",
        "storageId",
        "metadata",
        "linkSecurity",
        "asUsers",
        "folderSharing",
      ];

      nullableFields.forEach(fieldName => {
        if (isBlank(storage[fieldName]) || isEmptyArray(storage[fieldName])) {
          delete storage[fieldName];
        }
      });

      validateStep("cloudStorage", draft);
    });
  }

  if (payload.step === "filePath") {
    return produce(state, draft => {
      draft.steps.storageFilepath.done = false;
      const storage = updateFilepath(draft, payload.changes);

      // Remove properties with null values
      Object.getOwnPropertyNames(storage.location).forEach(fieldName => {
        if (isBlank(storage.location[fieldName])) {
          delete storage.location[fieldName];
        }
      });

      validateStep("storageFilepath", draft);
    });
  }

  if (payload.step === "approvalSteps") {
    return produce(state, draft => {
      draft.steps.approvalSteps.done = false;
      updateApprovalSteps(draft.rule, payload.changes);
      validateStep("approvalSteps", draft);
    });
  }

  return state;
}

function updateFilepath(draft: Draft<State>, changes: Partial<Storage>) {
  draft.steps.storageFilepath.done = false;
  const storage = getStorage(draft.rule);
  if ("location" in storage) {
    storage.location = {
      ...storage.location,
      ...changes.location,
    };
  }

  if ("collaboration" in changes) {
    if (!changes.collaboration) {
      delete storage.collaboration;
    } else {
      storage.collaboration = {
        ...storage.collaboration,
        ...changes.collaboration,
      };
    }
  }

  if ("override" in changes) {
    storage.override = changes.override;
  }

  return storage;
}

export type Action = {
  type: "updateStep";
  payload: UpdateStep;
};

export type StorageWebhookUpdate = StorageWebhook & {
  webhookStatus: WebhookCheckResult;
};

type UpdateStep =
  | {
      step: "action";
      changes: ActionState;
    }
  | {
      step: "target";
      changes:
        | Partial<EmailFlow | ArchivingAddress | Mailbox | StorageWebhookUpdate>
        | { saveForwardedMessages: boolean };
    }
  | {
      step: "cloudStorage";
      changes: Partial<Storage>;
    }
  | {
      step: "filePath";
      changes: Partial<Storage>;
    }
  | {
      step: "approvalSteps";
      changes: Partial<ApprovalSteps>;
    }
  | { step: "smimeAction"; changes: Partial<Smime> };

const getActionStatus = (state: State, payload: UpdateStep) => {
  const path = typePath(state.type, state.rule);
  const { changes } = payload;

  if (
    "linksFormat" in changes ||
    "trash" in changes ||
    "storageNotificationTemplateId" in changes
  ) {
    return false;
  }

  if ("contentToSave" in path && "content" in changes) {
    const { content: current } = path.contentToSave;
    const { content: update } = changes;
    const currentTypes = current.map(c => c.type).join("");
    const newTypes = update.map(c => c.type).join("");
    const isTypeChange = currentTypes !== newTypes;

    if (!isTypeChange) {
      state.steps.storageFilepath.done = false;
      state.steps.storageFilepath.valid = update.every(
        item => !isBlank(item.nameFormat)
      );
    }
    return !isTypeChange;
  }

  return false;
};

function hasOneAccount(accounts: ProviderAccounts) {
  return accounts.type === "ACCOUNT" && accounts.entities.length === 1;
}

function resetCopyFilesSetting(state: State, content: Content[]) {
  const mailbox = getMailbox(state.rule);

  if (!mailbox) return;

  if (isCopyFilesAllowed(content, state.rule.actionType)) return;

  delete mailbox.copyEmailFilesWhenNoAttachments;
  updateMailbox(state.rule, mailbox);
}
